import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Plus, Trash2, User } from 'lucide-react';
import { format } from 'date-fns';
import { notesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const ClientNotesTab = ({ 
  clientId, 
  notes, 
  canDeleteNote,
  onRefresh,
  toast 
}) => {
  const [noteDialog, setNoteDialog] = useState(false);
  const [newNote, setNewNote] = useState({ content: '', isPinned: false });
  const { user } = useAuth();

  const handleCreateNote = async () => {
    if (!newNote.content.trim()) {
      toast({ title: 'Error', description: 'Please enter note content', variant: 'destructive' });
      return;
    }
    try {
      await notesAPI.create({
        clientId: clientId,
        content: newNote.content,
        isPinned: newNote.isPinned,
        authorName: user?.name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Unknown'
      });
      toast({ title: 'Success', description: 'Note added successfully' });
      setNoteDialog(false);
      setNewNote({ content: '', isPinned: false });
      onRefresh();
    } catch (error) {
      console.error('Error creating note:', error);
      toast({ title: 'Error', description: 'Failed to add note', variant: 'destructive' });
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Are you sure you want to delete this note?')) return;
    try {
      await notesAPI.delete(noteId);
      toast({ title: 'Success', description: 'Note deleted successfully' });
      onRefresh();
    } catch (error) {
      console.error('Error deleting note:', error);
      toast({ title: 'Error', description: 'Failed to delete note', variant: 'destructive' });
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Notes</CardTitle>
            <Button size="sm" className="gap-2" onClick={() => setNoteDialog(true)} data-testid="add-note-btn">
              <Plus className="h-4 w-4" />
              Add Note
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {notes.length > 0 ? (
            <div className="space-y-3">
              {notes.map((note) => (
                <div key={note._id} className="p-4 border rounded-lg">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {note.isPinned && <Badge className="mb-2 bg-yellow-100 text-yellow-800">Pinned</Badge>}
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{note.content}</p>
                      <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                        {(note.authorName || note.createdBy) && (
                          <>
                            <User className="h-3 w-3" />
                            <span className="font-medium text-gray-500">{note.authorName || note.createdBy}</span>
                            <span className="mx-1">·</span>
                          </>
                        )}
                        {note.createdAt ? format(new Date(note.createdAt), 'MMM dd, yyyy h:mm a') : ''}
                      </p>
                    </div>
                    {canDeleteNote && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleDeleteNote(note._id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-gray-500 py-8">No notes yet</p>
          )}
        </CardContent>
      </Card>

      {/* Add Note Dialog */}
      <Dialog open={noteDialog} onOpenChange={setNoteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Note Content *</Label>
              <Textarea 
                placeholder="Enter your note..."
                value={newNote.content} 
                onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                rows={5}
              />
            </div>
            <div className="flex items-center gap-2">
              <input 
                type="checkbox" 
                id="pinNote"
                checked={newNote.isPinned}
                onChange={(e) => setNewNote(prev => ({ ...prev, isPinned: e.target.checked }))}
                className="rounded border-gray-300"
              />
              <Label htmlFor="pinNote" className="text-sm font-normal cursor-pointer">Pin this note</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateNote}>Add Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ClientNotesTab;
